import { chartTimeMilliseconds } from './timeRange';
import { z } from 'zod';
import type { ChartMarker, ChartRow, ChartScalar } from './types';

export type DisplayChartMarker = ChartMarker & {
  chartX: number;
  clusterMembers?: ChartMarker[];
  displayOffsetXPx?: number;
};

export type MarkerPositioner = (value: ChartMarker['x']) => number | null;

const coordinateSchema = z.union([z.string().min(1), z.number().finite()]);
const chartPositionSchema = z.number().finite();

export function createMarkerPositioner(
  rows: readonly ChartRow[],
  xKey: string,
  axisType: 'time' | 'number' | 'category' = 'time',
): MarkerPositioner {
  const exact = new Map<ChartMarker['x'], number>();
  const indexed = new Map<number, number>();
  const coordinate = (value: ChartScalar | undefined): number | null => {
    if (axisType === 'category') return null;
    const scalar = coordinateSchema.safeParse(value);
    if (!scalar.success) return null;
    if (axisType === 'time') return chartTimeMilliseconds(scalar.data);
    if (String(scalar.data).trim() === '') return null;
    const numeric = Number(scalar.data);
    return Number.isFinite(numeric) ? numeric : null;
  };
  rows.forEach((row) => {
    const value = row[xKey];
    const parsedChartX = chartPositionSchema.safeParse(row.__chartX);
    const parsedValue = coordinateSchema.safeParse(value);
    if (!parsedChartX.success || !parsedValue.success) return;
    const chartX = parsedChartX.data;
    if (!exact.has(parsedValue.data)) exact.set(parsedValue.data, chartX);
    const time = coordinate(value);
    if (time !== null && !indexed.has(time)) indexed.set(time, chartX);
  });
  const points = [...indexed].sort(([left], [right]) => left - right);

  return (value) => {
    const exactX = exact.get(value);
    if (exactX !== undefined) return exactX;
    const target = coordinate(value);
    if (
      target === null ||
      points.length === 0 ||
      target < points[0][0] ||
      target > points[points.length - 1][0]
    ) {
      return null;
    }
    let low = 0;
    let high = points.length - 1;
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      if (points[middle][0] === target) return points[middle][1];
      if (points[middle][0] < target) low = middle + 1;
      else high = middle - 1;
    }
    const before = points[high];
    const after = points[low];
    const fraction = (target - before[0]) / (after[0] - before[0]);
    return before[1] + fraction * (after[1] - before[1]);
  };
}

export function clusterVisibleMarkers(
  markers: readonly ChartMarker[],
  position: MarkerPositioner,
  viewport: { widthPx: number; minX: number; maxX: number; cellWidthPx?: number },
): DisplayChartMarker[] {
  const { widthPx, minX, maxX } = viewport;
  if (
    !Number.isFinite(widthPx) ||
    widthPx <= 0 ||
    !Number.isFinite(minX) ||
    !Number.isFinite(maxX) ||
    maxX < minX
  ) {
    return [];
  }
  const cellWidthPx =
    viewport.cellWidthPx && Number.isFinite(viewport.cellWidthPx) && viewport.cellWidthPx > 0
      ? viewport.cellWidthPx
      : 12;
  const span = maxX - minX;
  const groups = new Map<string, { chartX: number; members: ChartMarker[] }>();
  markers.forEach((marker) => {
    const chartX = position(marker.x);
    if (chartX === null || !Number.isFinite(chartX) || chartX < minX || chartX > maxX) return;
    const cell = span === 0 ? 0 : Math.floor((((chartX - minX) / span) * widthPx) / cellWidthPx);
    const key = JSON.stringify([
      marker.paneId,
      marker.seriesId,
      marker.details?.symbol ?? null,
      marker.side ?? null,
      marker.action ?? null,
      cell,
    ]);
    const group = groups.get(key);
    if (group) group.members.push(marker);
    else groups.set(key, { chartX, members: [marker] });
  });
  return [...groups.values()].flatMap(({ chartX, members }): DisplayChartMarker[] => {
    const first = members[0];
    if (members.length === 1) return [{ ...first, chartX }];
    if (members.length <= 3) {
      const pixelX = (x: number) => (span === 0 ? widthPx / 2 : ((x - minX) / span) * widthPx);
      const spacing = Math.min(44, Math.max(0, widthPx - 28) / (members.length - 1));
      const startPx = Math.max(
        14,
        Math.min(
          widthPx - 14 - (members.length - 1) * spacing,
          pixelX(chartX) - ((members.length - 1) * spacing) / 2,
        ),
      );
      return members.map((marker, index) => {
        const sourceX = position(marker.x)!;
        return {
          ...marker,
          chartX: sourceX,
          // Fan out small overlaps in screen space, keeping source dates/prices exact.
          displayOffsetXPx: startPx + index * spacing - pixelX(sourceX),
        };
      });
    }
    return [
      {
        ...first,
        id: `cluster:${first.id}:${members.length}`,
        label: `${members.length} fills`,
        chartX,
        clusterMembers: members,
      },
    ];
  });
}
